import React, { useState } from 'react'

import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { cn } from 'src/shared/utils/cn'

import { ImageDialog } from '../image-dialog'
interface ImageGridProps {
  images: string[]
  maxImages?: 2 | 5
  className?: string
  onImageClick?: (index: number) => void
  inverse?: boolean
}

const GridImage = ({
  src,
  index,
  onClick,
}: {
  src: string
  index: number
  onClick: (e: React.MouseEvent, index: number) => void
}) => {
  const isMobile = useIsMobile()
  return (
    <img
      src={src}
      alt={`Image ${index + 1}`}
      className={cn('size-full cursor-pointer rounded-lg border object-cover', isMobile && 'max-h-[350px]')}
      onClick={(e) => onClick(e, index)}
    />
  )
}

export const ImageGrid = ({ images, maxImages = 5, className, onImageClick, inverse }: ImageGridProps) => {
  const [imageDialogOpen, setImageDialogOpen] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  const handleImageClick = (e: React.MouseEvent, index: number) => {
    e.stopPropagation()
    setSelectedImageIndex(index)
    if (onImageClick) {
      onImageClick(index)
    } else {
      setImageDialogOpen(true)
    }
  }

  if (images.length === 0) return null

  const renderGrid = () => {
    switch (images.length) {
      case 1:
        return <GridImage src={images[0]} index={0} onClick={handleImageClick} />
      case 2:
        return (
          <div className={cn('flex h-[200px] gap-1', inverse && 'flex-row-reverse')}>
            {images.map((image, index) => (
              <div key={index} className="h-full w-1/2">
                <GridImage src={image} index={index} onClick={handleImageClick} />
              </div>
            ))}
          </div>
        )
      case 3:
        return (
          maxImages === 5 && (
            <div className={cn('flex h-[400px] gap-1', inverse && 'flex-row-reverse')}>
              <div className="h-full w-1/2">
                <GridImage src={images[0]} index={0} onClick={handleImageClick} />
              </div>
              <div className="flex w-1/2 flex-col gap-1">
                {images.slice(1).map((image, index) => (
                  <div key={index} className="h-1/2">
                    <GridImage src={image} index={index + 1} onClick={handleImageClick} />
                  </div>
                ))}
              </div>
            </div>
          )
        )
      case 4:
        return (
          maxImages === 5 && (
            <div className={cn('flex h-[400px] gap-1', inverse && 'flex-row-reverse')}>
              <div className="h-full w-3/5">
                <GridImage src={images[0]} index={0} onClick={handleImageClick} />
              </div>
              <div className="flex w-2/5 flex-col gap-1">
                {images.slice(1).map((image, index) => (
                  <div key={index} className="h-1/3">
                    <GridImage src={image} index={index + 1} onClick={handleImageClick} />
                  </div>
                ))}
              </div>
            </div>
          )
        )
      case 5:
        return (
          maxImages === 5 && (
            <div className="flex h-[450px] flex-col gap-1">
              <div className={cn('flex h-3/5 gap-1', inverse && 'flex-row-reverse')}>
                {images.slice(0, 2).map((image, index) => (
                  <div key={index} className="h-full w-1/2">
                    <GridImage src={image} index={index} onClick={handleImageClick} />
                  </div>
                ))}
              </div>
              <div className={cn('flex h-2/5 gap-1', inverse && 'flex-row-reverse')}>
                {images.slice(2).map((image, index) => (
                  <div key={index} className="h-full w-1/3">
                    <GridImage src={image} index={index + 2} onClick={handleImageClick} />
                  </div>
                ))}
              </div>
            </div>
          )
        )
      default:
        return null
    }
  }

  return (
    <>
      <div className={cn('max-h-[500px] overflow-hidden', className)}>{renderGrid()}</div>

      {!onImageClick && (
        <ImageDialog
          isOpen={imageDialogOpen}
          onClose={() => setImageDialogOpen(false)}
          images={images}
          initialImageIndex={selectedImageIndex}
        />
      )}
    </>
  )
}
